import PrimaryInnerGlowButton from '@/components/buttons/primary';
import SecondaryDefaultButton from '@/components/buttons/secondary';
import Image from '@/components/Image';
import { Container } from '@/components/layout/Container';
import Link from '@/components/Link';
import { PRIMARY_NAV_HEIGHT } from '@/components/navigation/PrimaryNavigation';
import Video from '@/components/Video';
import { AboutPageQuery } from '@/graphql';
import AboutPageQueryDocument from '@/graphql/queries/AboutPage.gql';
import { getSingleLink } from '@/lib/craft-utils';
import gtm from '@/lib/gtm';
import { getStaticPageProps } from '@/lib/next';
import { getSingle, maybeNotSet, notEmpty } from '@/lib/typescript-helpers';
import { COLORS } from '@/styles/color';
import { marginY, mediaBreakpointUp, paddingX, paddingY, square } from '@/styles/layout/utils';
import { FONT_WEIGHTS } from '@/styles/typography';
import { customTextSize, remCalc } from '@/styles/typography/utils';
import MasterTemplate from '@/templates/MasterTemplate';
import { Parser } from 'html-to-react';
import styled from 'styled-components';

const htmlToReactParser = new Parser();

const PageWrapper = styled.div``;

const Hero = styled.header`
  padding-top: ${remCalc(PRIMARY_NAV_HEIGHT)};

  ${Container} {
    ${paddingY(30, 50)}
  }

  h1 {
    ${customTextSize(32, 38)};
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
    letter-spacing: -0.01em;
  }

  ${mediaBreakpointUp('tablet')} {

    ${Container} {
      ${paddingY(60)}
      display: grid;
      grid-template-columns: repeat(12, 1fr);
      column-gap: ${remCalc(20)};
    }

    h1 {
      ${customTextSize(38, 46)};
      grid-column: 2 / span 10;
    }
  }

  ${mediaBreakpointUp('desktop')} {
    ${Container} {
      ${paddingY(80)}
      grid-template-columns: repeat(18, 1fr);
    }

    h1 {
      ${customTextSize(50, 58)};
      grid-column: 2 / span 13;
    }
  }

  ${mediaBreakpointUp('desktopWide')} {
    ${Container} {
      ${paddingY(160)};
    }

    h1 {
      ${customTextSize(68, 74)};
    }
  }
`;

const VideoContainer = styled.div`
  position: relative;
  grid-row: 1;
  grid-column: 2 / span 5;
  margin-top: ${remCalc(-30)};

  ${mediaBreakpointUp('tablet')} {
    grid-column: 8 / span 5;
    margin-top: ${remCalc(-40)};
    width: calc(100% + ${remCalc(20)});
    left: ${remCalc(-20)};
  }

  ${mediaBreakpointUp('desktop')} {
    grid-column: 11 / span 8;
    width: 100%;
    left: 0;
  }

  ${mediaBreakpointUp('desktopWide')} {
    margin-top: ${remCalc(-80)};
  }
`;

const StyledVideo = styled(Video)`
  border-radius: ${remCalc(10)};
  margin-bottom: ${remCalc(20)};

  ${mediaBreakpointUp('desktop')} {
    margin-bottom: ${remCalc(30)};
  }

  ${mediaBreakpointUp('desktopWide')} {
    margin-bottom: ${remCalc(50)};
  }
`;

const VideoQuote = styled.div`
  padding-left: ${remCalc(23)};

  blockquote {
    margin-bottom: ${remCalc(15)};

    p {
      position: relative;
      ${customTextSize(16, 24)};
      font-weight: ${FONT_WEIGHTS.MEDIUM};
      margin-bottom: ${remCalc(10)};

      // Red Quote
      &::before {
        content: '“';
        color: ${COLORS.RED.LOWLIGHT};
        position: absolute;
        left: ${remCalc(-23)};
        top: ${remCalc(-1)};
        ${customTextSize(38, 45)};
      }
    }

    footer {
      ${customTextSize(14, 20)};
    }
  }

  a {
    border-bottom: 2px solid;
    padding-bottom: 1px;
    ${customTextSize(14, 17)};
    font-weight: ${FONT_WEIGHTS.BOLD};
  }

  ${mediaBreakpointUp('desktop')} {
    blockquote {
      margin-bottom: ${remCalc(30)};

      p {
        ${customTextSize(24, 34)};
      }

      footer {
        ${customTextSize(20, 24)};
      }
    }

    a {
      ${customTextSize(16, 19)};
    }
  }
`;

const FeaturedSection = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  column-gap: ${remCalc(20)};
  row-gap: ${remCalc(50)};
  margin-bottom: ${remCalc(50)};

  ${mediaBreakpointUp('tablet')} {
    grid-template-columns: repeat(12, 1fr);
    row-gap: 0;
    margin-bottom: ${remCalc(100)};
  }

  ${mediaBreakpointUp('desktop')} {
    grid-template-columns: repeat(18, 1fr);
    margin-bottom: ${remCalc(160)};
  }

  ${mediaBreakpointUp('desktopWide')} {
    margin-bottom: ${remCalc(200)};
  }
`;

const ContentWrapper = styled.div`
  grid-row: 2;
  grid-column: 1 / span 5;

  p {
    ${customTextSize(14, 20)};
    margin-bottom: ${remCalc(16)};

    &:last-of-type {
      margin-bottom: 0;
    }
  }

  h2,
  h3 {
    ${customTextSize(16, 21)};
    font-weight: ${FONT_WEIGHTS.BOLD};
    color: ${COLORS.RED.LOWLIGHT};
    margin-bottom: ${remCalc(16)};

    &:not(:first-of-type) {
      margin-top: ${remCalc(30)};
    }
  }

  ${mediaBreakpointUp('tablet')} {
    grid-row: 1;
    grid-column: 2 / span 5;
    width: calc(100% + ${remCalc(20)});
    padding-top: ${remCalc(50)};
  }

  ${mediaBreakpointUp('desktop')} {
    grid-column: 2 / span 8;
    width: 100%;
    left: 0;

    p {
      ${customTextSize(16, 24)};
      margin-bottom: ${remCalc(32)};
    }

    h2,
    h3 {
      ${customTextSize(24, 34)};
      font-weight: ${FONT_WEIGHTS.SEMIBOLD};
      margin-top: ${remCalc(28)};
    }
  }

  ${mediaBreakpointUp('desktopWide')} {
    p {
      ${customTextSize(24, 34)};
      margin-bottom: ${remCalc(32)};
    }

    h2,
    h3 {
      ${customTextSize(32, 38)};
      margin-top: ${remCalc(34)};
      margin-bottom: ${remCalc(36)};
    }
  }
`;

const ImageWrapper = styled(Image)``;

const StatisticBlock = styled.div<{ index: any }>`
  position: relative;

  p {
    ${customTextSize(13, 20)};
    text-align: center;

    strong {
      display: block;
      ${customTextSize(38, 46)};
      letter-spacing: -0.01em;
      margin-bottom: ${remCalc(10)};
      font-weight: ${FONT_WEIGHTS.SEMIBOLD};

      span {
        position: relative;

        &::before,
        &::after {
          content: '';
          position: absolute;
          background-repeat: no-repeat;
          background-size: contain;
          ${square(36)}
        }

        &::before {
          background-image: url(${(props) => `'/assets/pages/about/stats_left_illustration_0${props.index}.svg'`});
          background-position: right center;
          right: calc(100% + 10px);
          ${(props) => ({ [props.index >= 3 ? 'bottom' : 'top']: remCalc(-5) })}
        }

        &::after {
          background-image: url(${(props) => `'/assets/pages/about/stats_right_illustration_0${props.index}.svg'`});
          background-position: left center;
          left: calc(100% + 10px);
          ${(props) => ({ [props.index >= 3 ? 'top' : 'bottom']: remCalc(-5) })}
        }
      }
    }
  }

  ${mediaBreakpointUp('tablet')} {
    p strong span {
      &::before,
      &::after {
        ${square(24)}
      }

      &::before {
        ${(props) => ({ [props.index >= 3 ? 'bottom' : 'top']: remCalc(-8) })}
      }

      &::after {
        ${(props) => ({ [props.index >= 3 ? 'top' : 'bottom']: remCalc(-8) })}
      }
    }
  }

  ${mediaBreakpointUp('desktop')} {
    p {
      ${customTextSize(16, 24)};

      strong {
        ${customTextSize(68, 80)};
        margin-bottom: ${remCalc(14)};

        span {
          &::before,
          &::after {
            ${square(40)}
          }

          &::before {
            ${(props) => ({ [props.index >= 3 ? 'bottom' : 'top']: remCalc(-5) })}
          }

          &::after {
            ${(props) => ({ [props.index >= 3 ? 'top' : 'bottom']: remCalc(-5) })}
          }
        }
      }
    }
  }

  ${mediaBreakpointUp('desktop')} {
    p {
      strong {
        ${customTextSize(80, 88)};
        margin-bottom: ${remCalc(20)};
      }
    }
  }
`;

const StatisticsSection = styled.section`
  position: relative;
  ${marginY(50)}
  display: flex;
  flex-direction: column;
  column-gap: ${remCalc(20)};
  row-gap: ${remCalc(30)};

  h2 {
    ${customTextSize(16, 21)};
    font-weight: ${FONT_WEIGHTS.BOLD};
    text-align: center;
  }

  ${mediaBreakpointUp('tablet')} {
    ${marginY(100)}
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;

    h2 {
      flex-basis: 100%;
    }

    ${StatisticBlock} {
      width: calc((100% - (${remCalc(20)} * 2)) / 2);
    }
  }

  ${mediaBreakpointUp('desktop')} {
    row-gap: ${remCalc(50)};
    ${marginY(160)}
    column-gap: ${remCalc(40)};

    h2 {
      ${customTextSize(24, 34)};
      font-weight: ${FONT_WEIGHTS.SEMIBOLD};
    }

    ${StatisticBlock} {
      width: calc((100% - (${remCalc(40)} * 2)) / 2);
    }
  }

  ${mediaBreakpointUp('desktopWide')} {
    ${marginY(200)};
    column-gap: ${remCalc(80)};

    h2 {
      ${customTextSize(32, 38)};
      margin-bottom: ${remCalc(30)};
    }

    ${StatisticBlock} {
      width: calc((100% - (${remCalc(80)} * 2)) / 2);
    }
  }
`;

const TeamPhoto = styled.div<{ objectPosition: string }>`
  position: relative;
  border-radius: 100%;
  overflow: hidden;
  width: 100%;
  aspect-ratio: 1 / 1;

  img {
    object-fit: cover;
    object-position: ${(props) => props.objectPosition};
  }
`;

const Head = styled.h5``;

const Body = styled.p``;

const TeamsBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  ${Head} {
    ${customTextSize(16, 25)};
    font-weight: ${FONT_WEIGHTS.BOLD};
    margin-top: ${remCalc(20)};
  }

  ${Body} {
    ${customTextSize(13, 20)};
    margin-top: ${remCalc(5)};
  }

  ${mediaBreakpointUp('desktop')} {
    ${Head} {
      margin-top: ${remCalc(30)};
    }

    ${Body} {
      ${customTextSize(14, 20)};
    }
  }

  ${mediaBreakpointUp('desktopWide')} {
    ${Head} {
      ${customTextSize(24, 34)};
      margin-top: ${remCalc(40)};
    }

    ${Body} {
      ${customTextSize(16, 24)};
      margin-top: ${remCalc(10)};
    }
  }
`;

const TeamSection = styled.section`
  ${marginY(50)}

  h3 {
    ${customTextSize(24, 30)};
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
    color: ${COLORS.RED.LOWLIGHT};
    text-align: center;
    margin-bottom: ${remCalc(30)};
  }

  > div {
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    column-gap: ${remCalc(20)};
    row-gap: ${remCalc(30)};
  }

  ${TeamsBlock} {
    grid-column: 2 / span 4;
  }

  ${mediaBreakpointUp('tablet')} {
    ${marginY(100, 50)}

    > div {
      grid-template-columns: repeat(12, 1fr);
    }

    ${TeamsBlock} {
      ${paddingX(10)}
      grid-column-start: auto;
      grid-column-end: span 4;
    }
  }

  ${mediaBreakpointUp('desktop')} {
    ${marginY(160, 80)}

    h3 {
      ${customTextSize(32, 38)};
    }

    > div {
      row-gap: ${remCalc(50)};
    }
  }

  ${mediaBreakpointUp('desktopWide')} {
    ${marginY(200, 100)}

    h3 {
      ${customTextSize(49, 58)};
      margin-bottom: ${remCalc(100)};
    }
  }
`;

const NewsBlock = styled.div`
  ${ImageWrapper} {
    margin-bottom: ${remCalc(20)};
  }

  a {
    ${customTextSize(16, 21)};
    font-weight: ${FONT_WEIGHTS.BOLD};
  }
`;

const NewsSection = styled.section`
  position: relative;
  ${marginY(50)}
  padding: ${remCalc(30)};

  h3 {
    ${customTextSize(24, 30)};
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
    color: ${COLORS.BLUE.LOWLIGHT};
    margin-bottom: ${remCalc(30)};
  }

  > div {
    display: grid;
    grid-template-columns: 1fr;
    grid-auto-flow: row;
    row-gap: ${remCalc(30)};
  }

  ${mediaBreakpointUp('tablet')} {
    ${marginY(50, 40)}
    padding: ${remCalc(40, 0)};

    display: grid;
    grid-template-columns: repeat(12, 1fr);
    column-gap: ${remCalc(20)};

    h3 {
      grid-column: 2 / -2;
      margin-bottom: ${remCalc(40)};
    }

    > div {
      grid-column: 2 / -2;
      grid-template-columns: 1fr 1fr;
      grid-auto-flow: row;
      column-gap: ${remCalc(20)};
    }
  }

  ${mediaBreakpointUp('desktop')} {
    ${marginY(80, 50)}
    padding: ${remCalc(60, 0, 80)};
    grid-template-columns: repeat(18, 1fr);

    h3 {
      ${customTextSize(32, 38)};
      margin-bottom: ${remCalc(58)};
    }

    > div {
      grid-template-columns: repeat(3, 1fr);
      row-gap: ${remCalc(50)};
    }
  }

  ${mediaBreakpointUp('desktopWide')} {
    padding: ${remCalc(80, 0)};
    ${marginY(100)}

    h3 {
      ${customTextSize(49, 58)};
    }

    ${NewsBlock} a {
      ${customTextSize(24, 34)};
      font-weight: ${FONT_WEIGHTS.SEMIBOLD};
    }
  }
`;

const SocialBlock = styled.div``;

const SocialBlockLogo = styled(Image)`
  width: auto;
  height: ${(props) => Math.min(parseInt(String(props.height!), 10), 33)}px;

  ${mediaBreakpointUp('desktopWide')} {
    height: ${(props) => Math.min(parseInt(String(props.height!), 10) * 1.33, 44)}px;
  }
`;

const SocialSection = styled.section`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  ${marginY(50, 40)}

  > div {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: ${remCalc(30)};
    width: 100%;
  }

  h3 {
    ${customTextSize(16, 20)};
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
    color: ${COLORS.GREEN.LOWLIGHT};
    margin-bottom: ${remCalc(30)};
    text-align: center;
  }

  ${mediaBreakpointUp('tablet')} {
    ${marginY(40, 30)}

    > div {
      column-gap: ${remCalc(40)};
      row-gap: ${remCalc(32)};
    }
  }

  ${mediaBreakpointUp('desktop')} {
    ${marginY(50)}

    h3 {
      ${customTextSize(24, 34)};
      margin-bottom: ${remCalc(40)};
    }
  }

  ${mediaBreakpointUp('desktopWide')} {
    ${marginY(100, 70)}

    h3 {
      ${customTextSize(32, 38)}
      margin-bottom: ${remCalc(70)};
    }

    > div {
      column-gap: ${remCalc(50)};
    }
  }
`;

const CustomerSection = styled.div`
  position: relative;
  ${marginY(40, 0)}

  h3 {
    ${customTextSize(16, 21)};
    font-weight: ${FONT_WEIGHTS.BOLD};
    margin-bottom: ${remCalc(30)};
    text-align: center;
  }

  p {
    ${customTextSize(13, 20)};
  }

  > div {
    display: flex;
    flex-direction: column;
    gap: ${remCalc(30, 20)};
  }

  ${SocialBlock} {
    padding: 0;
    flex-direction: column;
    text-align: center;
  }

  h6,
  p:last-child {
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  }

  ${mediaBreakpointUp('tablet')} {
    ${marginY(30, 0)}

    > div {
      flex-direction: row;
      flex-wrap: wrap;
      justify-content: center;
    }

    ${SocialBlock} {
      width: calc((100% - (${remCalc(20)} * 2)) / 3);
    }
  }

  ${mediaBreakpointUp('desktop')} {
    ${marginY(50, 0)}
    display: grid;
    grid-template-columns: repeat(18, 1fr);
    column-gap: ${remCalc(20)};

    h3 {
      grid-column: 1 / -1;
    }

    > div {
      grid-column: 2 / -2;
    }

    h3,
    h6 {
      ${customTextSize(16, 26)};
    }

    p {
      ${customTextSize(14, 20)};
    }

    ${SocialBlock} {
      width: calc((100% - (${remCalc(20)} * 3)) / 4);
    }
  }

  ${mediaBreakpointUp('desktopWide')} {
    ${marginY(70, 0)}

    > div {
      row-gap: ${remCalc(60)};
    }

    h3 {
      ${customTextSize(24, 34)};
      font-weight: ${FONT_WEIGHTS.SEMIBOLD};
      margin-bottom: ${remCalc(60)};
    }

    h6 {
      ${customTextSize(20, 24)};
    }

    p {
      ${customTextSize(14, 18)};
    }
  }
`;

const FooterSection = styled.div`
  ${paddingY(70, 50)}
  background-image: transparent;
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center 20%;

  ${Container} {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${remCalc(20)};
  }

  h3 {
    ${customTextSize(24, 30)};
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
    text-align: center;
    margin-bottom: ${remCalc(10)};
  }

  ${mediaBreakpointUp('tablet')} {
    ${paddingY(80, 40)};

    ${Container} {
      flex-direction: row;
      flex-wrap: wrap;
      justify-content: center;
    }

    h3 {
      flex: 1 0 100%;
    }
  }

  ${mediaBreakpointUp('desktop')} {
    ${paddingY(160, 80)}

    h3 {
      ${customTextSize(32, 38)}
      text-align: left;
      flex: 1 1 0;
      margin: 0 auto 0 0;
    }
  }

  ${mediaBreakpointUp('desktopWide')} {
    padding: ${remCalc(200, 100, 100)};
  }
`;

const AboutPage: PageTemplate<AboutPageQuery> = ({ entry }) => {
  const customerQuote = getSingle(entry?.customerQuote);
  const videoCoverImage = getSingle(entry?.videoCoverImage);
  const heroCta = getSingleLink(entry?.heroCta);
  const footerCTA = getSingleLink(entry?.footerCTA);
  const extraFooterCta = getSingleLink(entry?.extraFooterCta);

  let svgIndex = 0;

  return (
    <PageWrapper>
      <Hero>
        <Container>
          <h1>{entry?.heroHeader! ?? ''}</h1>
        </Container>
      </Hero>

      <Container>
        <FeaturedSection>
          <VideoContainer>
            <StyledVideo
              videoUrl={getSingle(entry?.video)?.url ?? ''}
              coverSrc={videoCoverImage?.url! ?? ''}
              captionsUrl={getSingle(entry?.videoSubtitles)?.url}
              showPlayButton
            />

            <VideoQuote>
              <blockquote>
                {htmlToReactParser.parse(customerQuote?.quote! ?? '')}

                <footer>
                  {htmlToReactParser.parse(customerQuote?.authorName! ?? '')}, {customerQuote?.authorTitle! ?? ''}
                </footer>
              </blockquote>

              {heroCta ? (
                <Link href={heroCta.url}>
                  <a>{heroCta.text}</a>
                </Link>
              ) : null}
            </VideoQuote>
          </VideoContainer>

          <ContentWrapper>{htmlToReactParser.parse(entry?.richContent ?? '')}</ContentWrapper>
        </FeaturedSection>

        <StatisticsSection>
          <h2>{entry?.statisticsHeader ?? ''}</h2>

          {entry?.statistics?.map((stat, index) => {
            {
              /*
               * ALERT: This "solution" forces images to match with a set order in Craft,
               * so the map array index value aligns with the image filename (starting with 1).
               * The original, default solution is that svgIndex is simply: index + 1
               * If this ever needs to be changed, pay close attention to the next line of code
               */
              index < 2 ? (svgIndex = index + 1) : (svgIndex = index + 2);
            }
            return (
              <StatisticBlock key={stat?.id!} index={svgIndex}>
                <p>
                  <strong>
                    <span>{stat?.statValue ?? ''}</span>
                  </strong>{' '}
                  {stat?.statText ?? ''}
                </p>
              </StatisticBlock>
            );
          })}
        </StatisticsSection>

        <TeamSection>
          <h3>{htmlToReactParser.parse(entry?.teamHeader! ?? '')}</h3>
          <div>
            {entry?.teamTable?.map((person) => {
              const photo = getSingle(person?.personPhoto);

              return (
                <TeamsBlock key={photo?.id ?? ''}>
                  <TeamPhoto objectPosition="center 12%">
                    <Image src={photo?.url ?? ''} alt="" fill />
                  </TeamPhoto>
                  <Head>{person?.personName ?? ''}</Head>
                  <Body>{person?.personTitle ?? ''}</Body>
                </TeamsBlock>
              );
            })}
          </div>
        </TeamSection>

        <NewsSection>
          <h3>{htmlToReactParser.parse(entry?.newsHeader! ?? '')}</h3>

          <div>
            {entry?.pressArticles?.filter(notEmpty).map((article, i) => {
              const logo = getSingle(article?.publisherLogo);

              return (
                <NewsBlock key={article.articleUrl ?? i}>
                  {logo && (
                    <ImageWrapper
                      key={logo.id!}
                      src={logo.url ?? ''}
                      width={Math.ceil((logo.width! / logo.height!) * 20)}
                      height={26}
                      alt=""
                    />
                  )}

                  <a
                    href={article.articleUrl ?? '#'}
                    onClick={() => {
                      gtm.track('click', {
                        element_type: 'link',
                        element_label: maybeNotSet(article.articleTitle),
                        location: 'Press Articles',
                      });
                    }}
                  >
                    {article.articleTitle ?? ''}
                  </a>
                </NewsBlock>
              );
            })}
          </div>
        </NewsSection>

        <SocialSection>
          <h3>{htmlToReactParser.parse(entry?.investorHeading ?? '')}</h3>

          <div>
            {entry?.investorLogos?.filter(notEmpty).map((logo, i) => (
              <SocialBlockLogo key={logo.url ?? i} src={logo.url ?? ''} width={logo.width ?? 0} height={logo.height ?? 0} alt="" />
            ))}
          </div>
        </SocialSection>

        <CustomerSection>
          <h3>{htmlToReactParser.parse(entry?.customerHeader! ?? '')}</h3>
          <div>
            {entry?.investorList?.map((investor, i) => {
              return (
                <SocialBlock key={investor?.investorName ?? i}>
                  <h6>{investor?.investorName ?? ''}</h6>
                  <p>{investor?.investorTitle ?? ''}</p>
                  <p>{investor?.investorCompany ?? ''}</p>
                </SocialBlock>
              );
            })}
          </div>
        </CustomerSection>
      </Container>

      {footerCTA || extraFooterCta ? (
        <FooterSection>
          <Container>
            <h3>{htmlToReactParser.parse(entry?.footerHeader! ?? '')}</h3>

            {footerCTA ? (
              <Link href={footerCTA.url}>
                <PrimaryInnerGlowButton>{footerCTA.text}</PrimaryInnerGlowButton>
              </Link>
            ) : null}

            {extraFooterCta ? (
              <Link href={extraFooterCta.url}>
                <SecondaryDefaultButton>{extraFooterCta.text}</SecondaryDefaultButton>
              </Link>
            ) : null}
          </Container>
        </FooterSection>
      ) : null}
    </PageWrapper>
  );
};

AboutPage.template = MasterTemplate;

export default AboutPage;

export const getStaticProps = getStaticPageProps('about', async ({ client }) => {
  const props = await client.request<AboutPageQuery>(AboutPageQueryDocument);

  return { props };
});
